"use client";

/**
 * Applies the SERVED experience to the app (FN experience seam, slice 1).
 *
 * THE DECLARED DESIGN DECISION, so nobody "fixes" it into a defect later:
 * the served payload's `surface` key SELECTS this app's own brand palette
 * (data-brand -> globals.css), and the served hex tokens are NOT painted
 * over it. This app's palettes are a 19-token design system (hover
 * states, CTA variants, pales) that the B1 work curated per brand; the
 * server's 10 canonical tokens are the VITE app's rendering contract,
 * where they are the complete palette. Overriding 10 of 19 here would
 * produce a mongrel palette that neither designer chose. If the server
 * ever becomes the token authority for THIS app, that is a design
 * decision for the brand owner, not a silent code change — the raw
 * `branding` record rides the context untouched for that day.
 *
 * State semantics mirror the backend's own three-way split:
 *  - default payload (surface null) -> DEFAULT_BRAND, byte-identical to
 *    the pre-slice-1 hardcoded behavior;
 *  - served surface -> data-brand flips (BrandLogo re-renders via its
 *    MutationObserver; the whole palette cascades from globals.css) and
 *    the tab title takes the served name;
 *  - 503 / transport failure -> status "unavailable", default brand kept,
 *    console.error so the state is LOUD in every environment. Gate
 *    consumers (gates.ts) fall back to DEFAULT-VISIBLE on "unavailable" —
 *    the PLATFORM posture, matching the Vite app's documented rule, and
 *    corrected here from an earlier fail-closed note: gates are
 *    subtractive brand configuration the server enforces regardless, and
 *    the two front ends must fail the same way. The context still refuses
 *    to pretend it is ready.
 *
 * #411 (2026-09-07): the root layout now resolves the experience on the
 * SERVER (shared/experience/server.ts) and passes it in as `initial`. With
 * an initial payload the provider starts READY — no client round-trip, no
 * default-brand frame — and the page title comes from generateMetadata, which
 * retires the limitation this comment used to record (title set
 * post-hydration and reset by App Router navigations). Without `initial`
 * (server-side failure, or a caller that does not have it) the client
 * bootstrap below runs exactly as before.
 */
import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

import { ServedBrandContext } from "../ui/servedBrand";
import { applyBrand, type BrandId } from "../ui/tokens";
import {
  fetchExperienceBootstrap,
  ExperienceUnavailableError,
  type ExperienceBootstrap,
} from "./bootstrap";

// The surface -> palette mapping lives in a plain (server-safe) module since
// #411 so the root layout can call it; re-exported here for client importers.
import { brandIdForSurface } from "./surfaceBrand";
export { brandIdForSurface };

/** The palette a served surface selects; the default surface (null) and an
 *  unknown surface both keep the default palette. */
function brandFor(surface: string | null): BrandId {
  return brandIdForSurface(surface);
}

export type ExperienceStatus = "loading" | "ready" | "unavailable";

export interface ExperienceContextValue {
  status: ExperienceStatus;
  /** Null while loading, on the default surface, and on failure. */
  surface: string | null;
  name: string | null;
  branding: Readonly<Record<string, string>>;
  featureGates: ExperienceBootstrap["featureGates"];
  catalog: Readonly<Record<string, unknown>>;
}

const EMPTY_GATES = { hiddenSteps: [], disabledFeatures: [] } as const;

const INITIAL: ExperienceContextValue = {
  status: "loading",
  surface: null,
  name: null,
  branding: {},
  featureGates: EMPTY_GATES,
  catalog: {},
};

const ExperienceContext = createContext<ExperienceContextValue>(INITIAL);

export function useExperience(): ExperienceContextValue {
  return useContext(ExperienceContext);
}

function readyState(payload: ExperienceBootstrap): ExperienceContextValue {
  return {
    status: "ready",
    surface: payload.surface,
    name: payload.name,
    branding: payload.branding,
    featureGates: payload.featureGates,
    catalog: payload.catalog,
  };
}

export function ExperienceProvider({
  children,
  initial = null,
}: {
  children: React.ReactNode;
  /** #411: the experience the SERVER resolved for this request, or null to
   *  bootstrap client-side as before. */
  initial?: ExperienceBootstrap | null;
}) {
  const [state, setState] = useState<ExperienceContextValue>(() =>
    initial ? readyState(initial) : INITIAL,
  );

  useEffect(() => {
    if (initial) {
      // The server already rendered <html data-brand> from this payload;
      // re-applying is idempotent and keeps the attribute authoritative for
      // the MutationObserver consumers if anything else touched it.
      applyBrand(document.documentElement, brandFor(initial.surface));
      return;
    }
    let cancelled = false;
    fetchExperienceBootstrap()
      .then((payload) => {
        if (cancelled) return;
        const root = document.documentElement;
        applyBrand(root, brandFor(payload.surface));
        if (payload.name) document.title = payload.name;
        setState({
          status: "ready",
          surface: payload.surface,
          name: payload.name,
          branding: payload.branding,
          featureGates: payload.featureGates,
          catalog: payload.catalog,
        });
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        // LOUD in every environment; the brand stays default rather than
        // guessing, and the status tells consumers not to trust gates.
        if (err instanceof ExperienceUnavailableError) {
          console.error(
            "experience bootstrap: host is mapped but unresolvable (503)",
          );
        } else {
          console.error("experience bootstrap failed", err);
        }
        setState((prev) => ({ ...prev, status: "unavailable" }));
      });
    return () => {
      cancelled = true;
    };
  }, [initial]);

  const value = useMemo(() => state, [state]);
  return (
    <ExperienceContext.Provider value={value}>
      <ServedBrandContext.Provider
        value={state.status === "ready" ? brandFor(state.surface) : null}
      >
        {children}
      </ServedBrandContext.Provider>
    </ExperienceContext.Provider>
  );
}

export default ExperienceProvider;
